import React, { ComponentProps, useRef, useState } from 'react';

import { NavLink } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, ClickAwayListener, IconButton, Menu, MenuItem, useMediaQuery } from '@mui/material';

import TENANTS from '~/consts/tenant';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import useHoverMenu from '~/hooks/useHoverMenu';
import useToggleState from '~/hooks/useToggleState';

import TenantIcon from './SiteHeader/TenantIcon';

const tenantEntries = Object.entries(TENANTS);

interface Props {
  size?: ComponentProps<typeof Button>['size'];
  variant?: ComponentProps<typeof Button>['variant'];
}

function TenantSelect(props: Props) {
  const { size, variant } = props;
  const { tenant: currentTenant, onTenantSelect } = useCurrentTenant();

  const [wasOpenedWithKeyboard, setWasOpenedWithKeyboard] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const isHoverCapable = useMediaQuery('(hover: hover)');
  const { isToggled: isMenuOpen, toggleOn, toggleOff: closeMenu } = useToggleState();

  const openMenu = (openedWithKeyboard = false) => {
    setWasOpenedWithKeyboard(openedWithKeyboard);
    toggleOn();
  };

  const hoverMenuHandlers = useHoverMenu(() => openMenu(), closeMenu);

  const openMenuWithKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    openMenu(true);
  };

  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex' }} {...hoverMenuHandlers}>
        <IconButton aria-label="Change tenant" color="inherit" component={NavLink} to="/">
          <TenantIcon />
        </IconButton>
        <Button
          ref={menuTriggerRef}
          size={size}
          variant={variant}
          color="inherit"
          sx={{
            flexShrink: 0,
            minWidth: 0,
            p: 0,
            '& .MuiButton-endIcon': { ml: 0 },
          }}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={() => openMenu()}
          onKeyDown={openMenuWithKeyboard}
          aria-label="Open tenant menu"
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
        />
      </Box>
      <ClickAwayListener onClickAway={closeMenu}>
        <Menu
          open={isMenuOpen}
          anchorEl={menuTriggerRef.current}
          autoFocus={wasOpenedWithKeyboard}
          onClose={closeMenu}
          MenuListProps={{ ...hoverMenuHandlers, autoFocusItem: wasOpenedWithKeyboard }}
          sx={
            isHoverCapable
              ? {
                  pointerEvents: 'none',
                  '& .MuiMenu-paper': { pointerEvents: 'auto' },
                }
              : undefined
          }
        >
          {tenantEntries.map(([key, tenant]) => (
            <MenuItem
              key={key}
              defaultValue={tenant.value}
              selected={tenant.value === currentTenant.value}
              disabled={tenant.value === currentTenant.value}
              onClick={() => {
                onTenantSelect(tenant.value);
                closeMenu();
              }}
            >
              {tenant.title}
            </MenuItem>
          ))}
        </Menu>
      </ClickAwayListener>
    </>
  );
}

export default TenantSelect;
